import { useEffect } from 'react';

/**
 * Warns before leaving a form with unsaved changes.
 *
 * Task 21 requires that "unsaved changes are warned about". Two exits need
 * covering and only one of them is React's: `beforeunload` catches a closed
 * tab or a typed URL, and the router blocker catches in-app navigation.
 *
 * This handles the browser exit. In-app navigation is the router's `useBlocker`
 * and belongs to whichever screen owns the form, because only it knows what
 * "discard" should do.
 *
 * The browser shows its own generic wording; a custom message has been ignored
 * by every major browser for a decade, so none is attempted.
 */
export function useUnsavedGuard(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;

    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Assigning returnValue is what actually triggers the prompt; the value
      // itself is discarded by the browser. The property is deprecated in the
      // spec but still the only cross-browser signal for beforeunload.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);
}
